/**
 * INVENTORY LIFECYCLE FLOW (single source of truth: backend at API_BASE_URL)
 *
 * 1. UI form → addProduct() / updateProduct()
 * 2. apiPost/apiPut(API_BASE_URL, ...) → backend; DB owned by backend.
 * 3. Read: loadProducts() → setProducts(); never clear list before fetch (Phase 2 stability).
 * 4. Cache: localStorage / IndexedDB — NOT source of truth.
 *
 * STABILITY (Phase 4): Add product uses optimistic UI — insert temp with _pending, then replace with
 * server response on success; on failure remove temp and show error. No full refetch after add.
 * No "saved" without confirmed 2xx. Offline path still uses local-first; ADD_PRODUCT_SAVED_LOCALLY for that flow.
 */
import React, { createContext, useContext, useState, useRef, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Product } from '../types';
import { getStoredData, setStoredData, isStorageAvailable } from '../lib/storage';
import { API_BASE_URL } from '../lib/api';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/apiClient';
import { getUserFriendlyMessage, INVENTORY_ERRORS } from '../lib/errorMessages';
import { getApiCircuitBreaker } from '../lib/circuit';
import { useWarehouse, DEFAULT_WAREHOUSE_ID } from './WarehouseContext';
import { isValidWarehouseId } from '../lib/warehouseId';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext';
import { getCategoryDisplay, normalizeProductLocation } from '../lib/utils';
import { parseProductsResponse } from '../lib/apiSchemas';
import { getProductImages, setProductImages } from '../lib/productImagesStore';
import { stripHeavyImagesForCache } from '../lib/productCacheImages';
import {
  PRODUCTS_CACHE_TTL_MS,
  SILENT_REFRESH_THROTTLE_MS,
  RECENT_ADD_WINDOW_MS,
  RECENT_UPDATE_WINDOW_MS,
  RECENT_DELETE_WINDOW_MS,
  SIZE_UPDATE_COOLDOWN_MS,
  INVENTORY_POLL_MS,
  INVENTORY_PAGE_SIZE,
  INVENTORY_API_TIMEOUT_MS,
  LAST_UPDATED_PRESERVE_MS,
} from '../constants/inventory';
import { sanitizeQuantityBySizeForApi } from '../lib/sizeCode';

/** Per-warehouse cache key so we can show the right list immediately on login/refresh. */
function productsCacheKey(warehouseId: string): string {
  return `warehouse_products_${warehouseId}`;
}

/** Seed/placeholder product ID that may exist in cache but not on production API; skip verify to avoid 404. */
const SEED_PLACEHOLDER_PRODUCT_ID = '00000000-0000-0000-0000-000000000101';

/** Normalize raw cache entry to Product (dates + location). Used for initial state and cache read. */
function normalizeProductFromRaw(p: any): Product {
  return normalizeProductLocation({
    ...p,
    createdAt: p?.createdAt ? new Date(p.createdAt) : new Date(),
    updatedAt: p?.updatedAt ? new Date(p.updatedAt) : new Date(),
    expiryDate: p?.expiryDate ? new Date(p.expiryDate) : null,
  });
}

/** True when quantityBySize is exactly one row and it's the RPC fallback "One size" / "ONESIZE" (so we should prefer cache's real sizes). */
function isOnlySyntheticOneSize(quantityBySize: unknown): boolean {
  if (!Array.isArray(quantityBySize) || quantityBySize.length !== 1) return false;
  const code = String((quantityBySize[0] as { sizeCode?: string })?.sizeCode ?? '').trim().replace(/\s+/g, '').toUpperCase();
  return code === 'ONESIZE' || code === 'ONE_SIZE';
}

/** Read cached product list for this warehouse only. Never returns another warehouse's data — so switching warehouse always shows correct scope (or loading until API returns). */
function getCachedProductsForWarehouse(warehouseId: string): Product[] {
  if (typeof window === 'undefined' || !isStorageAvailable()) return [];
  try {
    const list: any[] = getStoredData<any[]>(productsCacheKey(warehouseId), []);
    if (!Array.isArray(list) || list.length === 0) return [];
    const out: Product[] = [];
    for (const p of list) {
      if (p == null || typeof p !== 'object') continue;
      try {
        out.push(normalizeProductFromRaw(p));
      } catch {
        /* skip malformed */
      }
    }
    return out;
  } catch {
    return [];
  }
}
import { reportError } from '../lib/errorReporting';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import { INVENTORY_UPDATED_EVENT } from '../lib/inventoryEvents';
import {
  syncProductsListToQueryCache,
  getProductsListFromQueryCache,
  posProductsToInventoryProducts,
  invalidateProductsQuery,
} from '../lib/productsQueryCache';
import {
  logInventoryCreate,
  logInventoryUpdate,
  logInventoryRead,
  logInventoryDelete,
} from '../lib/inventoryLogger';

interface InventoryContextType {
  products: Product[];
  isLoading: boolean;
  error: string | null;
  addProduct: (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'> & { warehouseId?: string }) => Promise<string>;
  updateProduct: (id: string, updates: Partial<Product> & { warehouseId?: string }) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  deleteProducts: (ids: string[]) => Promise<void>;
  undoAddProduct: (productId: string) => Promise<void>;
  getProduct: (id: string) => Product | undefined;
  searchProducts: (query: string) => Product[];
  filterProducts: (filters: ProductFilters) => Product[];
  refreshProducts: (options?: { silent?: boolean; bypassCache?: boolean; timeoutMs?: number }) => Promise<void>;
  /** Total rows for this warehouse from the list API (`total`); null offline or unknown. */
  productsTotal: number | null;
  /** True when a background (silent) refresh is in progress — show "Updating..." indicator. */
  isBackgroundRefreshing: boolean;
  /** Push products that exist only in this browser's storage to the API so they appear everywhere. */
  syncLocalInventoryToApi: () => Promise<{ synced: number; failed: number; total: number; syncedIds: string[] }>;
  /** Number of products that exist only on this device (not yet on server). Always 0 when API is source of truth. */
  unsyncedCount: number;
  /** Last time product list was successfully loaded from server (for "Updated X ago"). */
  lastSyncAt: Date | null;
  /** True if this product id was saved only locally (API failed). Use to show "Local only" badge. */
  isUnsynced: (productId: string) => boolean;
  /** Re-fetch product from server to check if it was saved. Returns { saved, product }. If saved, updates local state and clears unsynced flag. */
  verifyProductSaved: (productId: string) => Promise<{ saved: boolean; product?: Product }>;
  /** True if the last attempt to save inventory to this device's local storage failed (e.g. private mode, quota). */
  storagePersistFailed: boolean;
  /** Current save phase for product form: idle | saving | verifying. Use for button label (Saving… / Verifying…). */
  savePhase: 'idle' | 'saving' | 'verifying';
  /** True when more products can be loaded (API pagination). When false, InventoryPage does not show "Load more". */
  hasMore: boolean;
  /** Load next page of products. No-op when hasMore is false. */
  loadMore: () => Promise<void>;
  /** True while loadMore is in progress. */
  isLoadingMore: boolean;
}

function readTotalFromProductsResponse(raw: { data?: Product[]; total?: number } | Product[] | null): number | undefined {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw) && 'total' in raw) {
    const t = (raw as { total?: number }).total;
    if (typeof t === 'number') return t;
  }
  return undefined;
}

export interface ProductFilters {
  category?: string;
  minQuantity?: number;
  maxQuantity?: number;
  lowStock?: boolean;
  outOfStock?: boolean;
  tag?: string;
}

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

/** Message thrown when addProduct saves locally due to API failure. UI closes modal and shows warning toast. */
export const ADD_PRODUCT_SAVED_LOCALLY =
  'Product saved locally. It will sync to the server when connection is available.';

/** Hunnid Official: only Main Jeff and Hunnid Main. We always use the selected warehouse id so products load for both. */
export function InventoryProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { currentWarehouseId } = useWarehouse();
  const { showToast } = useToast();
  const { tryRefreshSession } = useAuth();
  const effectiveWarehouseId = isValidWarehouseId(currentWarehouseId) ? currentWarehouseId : DEFAULT_WAREHOUSE_ID;

  const [apiOnlyProducts, setApiOnlyProductsState] = useState<Product[]>([]);
  const [apiOnlyLoading, setApiOnlyLoadingState] = useState(true);

  /** Refs to keep refreshProducts stable and avoid re-run loops when server is down (prevents list jitter). */
  const loadProductsRef = useRef<
    (signal?: AbortSignal, options?: { silent?: boolean; bypassCache?: boolean; timeoutMs?: number; append?: boolean; requestLimit?: number }) => Promise<void>
  >(() => Promise.resolve());
  /** Mirror of current products for equivalence check during silent refresh (avoids setState when nothing changed → no jitter). */
  const productsRef = useRef<Product[]>([]);
  const effectiveWarehouseIdRef = useRef(effectiveWarehouseId) as React.MutableRefObject<string>;
  useEffect(() => {
    effectiveWarehouseIdRef.current = effectiveWarehouseId;
  }, [effectiveWarehouseId]);
  /** Throttle silent refresh so poll + visibility + mount don't cause back-to-back requests (reduces list jitter). */
  const lastSilentRefreshAtRef = useRef<number>(0);

  const products = useMemo((): Product[] => apiOnlyProducts, [apiOnlyProducts]);
  /** Merge in client-saved images so they stay visible even when API/refresh omits them. */
  const productsWithLocalImages = useMemo(
    () =>
      products.map((p) => {
        const fromApi = Array.isArray(p.images) ? p.images : [];
        const fromLocal = getProductImages(p.id);
        const images = fromApi.length > 0 ? fromApi : (fromLocal ?? []);
        return { ...p, images };
      }),
    [products]
  );
  const isLoading = apiOnlyLoading;

  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  const setProducts = useCallback(
    (arg: Product[] | ((prev: Product[]) => Product[])) => {
      setApiOnlyProductsState(typeof arg === 'function' ? (arg as (prev: Product[]) => Product[])(apiOnlyProducts) : arg);
    },
    [apiOnlyProducts]
  );
  const setIsLoading = useCallback((value: boolean) => {
    setApiOnlyLoadingState(value);
  }, []);

  const [error, setError] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const mountedRef = useRef(true);
  /** Ids of products saved only locally (API failed). Kept for backward compat; unsyncedCount now from hook. */
  const [localOnlyIds, setLocalOnlyIds] = useState<Set<string>>(() => new Set());
  /** Set when setStoredData returns false so UI can warn that local storage may not have been updated. */
  const [storagePersistFailed, setStoragePersistFailed] = useState(false);
  /** Save phase for product form so button can show Saving… / Verifying… */
  const [savePhase, setSavePhase] = useState<'idle' | 'saving' | 'verifying'>('idle');
  /** True while a silent (background) refresh is in progress — for "Updating..." indicator. */
  const [isBackgroundRefreshing, setBackgroundRefreshing] = useState(false);
  /** Server total from GET /api/products (`total`); required for Load more. */
  const [productsTotal, setProductsTotal] = useState<number | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreInflightRef = useRef(false);
  /** Prevents overlapping main list fetches; pair with loadProductsAbortRef. */
  const loadProductsInflightRef = useRef(false);
  const loadProductsAbortRef = useRef<AbortController | null>(null);
  const syncRef = useRef<(() => Promise<{ synced: number; failed: number; total: number; syncedIds: string[] }>) | null>(null);

  // Persist current products to localStorage for legacy/fallback (products now from Dexie via hook)
  const persistProducts = useCallback(
    (next: Product[]) => {
      if (!isStorageAvailable() || !next?.length) return;
      try {
        const ok = setStoredData(productsCacheKey(effectiveWarehouseId), stripHeavyImagesForCache(next));
        if (!ok) setStoragePersistFailed(true);
      } catch (e) {
        reportError(e instanceof Error ? e : new Error(String(e)), { context: 'persistProducts', listLength: next.length });
      }
    },
    [effectiveWarehouseId]
  );
  useEffect(() => {
    if (products.length > 0) persistProducts(products);
  }, [products, persistProducts]);

  /** Keep React Query product cache aligned with Inventory list (POS reads same key). */
  useEffect(() => {
    if (!isValidWarehouseId(effectiveWarehouseId)) return;
    syncProductsListToQueryCache(
      queryClient,
      effectiveWarehouseId,
      productsWithLocalImages,
      productsTotal
    );
  }, [queryClient, effectiveWarehouseId, productsWithLocalImages, productsTotal]);

  const cacheRef = useRef<Record<string, { data: Product[]; ts: number }>>({});
  /** Throttle "Showing cached data" toast to once per 15s so multiple failed loads don't stack. */
  const lastCachedToastAtRef = useRef<number>(0);
  const CACHED_TOAST_COOLDOWN_MS = 15_000;
  /** Recently added product so loadProducts never overwrites and drops it; also used to pin to top to avoid jitter. */
  const lastAddedProductRef = useRef<{ product: Product; at: number } | null>(null);
  /** Recently updated product (with images) so any subsequent loadProducts (refresh/sync/mount) keeps the image. */
  const lastUpdatedProductRef = useRef<{ product: Product; at: number } | null>(null);
  /** Ids deleted in the last window so in-flight loadProducts cannot re-add them (avoids delete "flashing back"). */
  const recentlyDeletedIdsRef = useRef<Set<string>>(new Set());
  const lastSizeUpdateAtRef = useRef<number>(0);

  const productsPath = (base: string, opts?: { limit?: number; offset?: number; q?: string; category?: string; low_stock?: boolean; out_of_stock?: boolean; view?: 'list' }) => {
    const params = new URLSearchParams();
    params.set('warehouse_id', effectiveWarehouseId);
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    if (opts?.offset != null) params.set('offset', String(opts.offset));
    if (opts?.q) params.set('q', opts.q);
    if (opts?.category) params.set('category', opts.category);
    if (opts?.low_stock) params.set('low_stock', 'true');
    if (opts?.out_of_stock) params.set('out_of_stock', 'true');
    if (opts?.view === 'list') params.set('view', 'list');
    const qs = params.toString();
    return `${base}${base.includes('?') ? '&' : '?'}${qs}`;
  };

  /**
   * Clear old mock data from localStorage (transactions/orders only).
   * Never touch warehouse_products — user-recorded inventory must persist forever.
   */
  const clearMockData = () => {
    if (!isStorageAvailable()) return;
    const keysToRemove = ['transactions', 'orders', 'offline_transactions'];
    
    keysToRemove.forEach(key => {
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          
          // Check if it's an array (products, orders, transactions)
          if (Array.isArray(parsed)) {
            // Check if it contains mock data indicators
            const isMockData = parsed.some((item: any) => 
              item.sku?.includes('SKU-2024') || 
              item.createdBy === 'admin' ||
              item.createdBy === 'manager' ||
              item.createdBy === 'cashier' ||
              (item.sku && item.sku.startsWith('SKU-2024'))
            );
            
            if (isMockData) {
              localStorage.removeItem(key);
            }
          } else if (parsed && typeof parsed === 'object') {
            // For single objects, check if they have mock indicators
            if (parsed.sku?.includes('SKU-2024') || parsed.createdBy === 'admin') {
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          reportError(e instanceof Error ? e : new Error(String(e)), { context: 'clearMockData', key });
          localStorage.removeItem(key);
        }
      }
    });
  };

  /** Normalize product from API or localStorage: dates, location, images array, and numeric fields so list and totals are accurate. */
  /** Always set sizeKind and quantityBySize so Sizes column/card never show blank for One size / Multiple sizes. */
  const normalizeProduct = (p: Record<string, unknown>): Product => {
    const normalized: Record<string, unknown> = {
      ...p,
      images: Array.isArray(p.images) ? p.images : [],
      quantity: Number(p.quantity ?? 0) || 0,
      costPrice: Number(p.costPrice ?? p.cost_price ?? 0) || 0,
      sellingPrice: Number(p.sellingPrice ?? p.selling_price ?? 0) || 0,
      reorderLevel: Number(p.reorderLevel ?? p.reorder_level ?? 0) || 0,
      createdAt: p.createdAt ? new Date(p.createdAt as string) : new Date(),
      updatedAt: p.updatedAt ? new Date(p.updatedAt as string) : new Date(),
      expiryDate: p.expiryDate ? new Date(p.expiryDate as string) : null,
      sizeKind: (p.sizeKind ?? p.size_kind ?? 'na') as 'na' | 'one_size' | 'sized',
      quantityBySize: Array.isArray(p.quantityBySize) ? p.quantityBySize : [],
    };
    return normalizeProductLocation(normalized as { location?: unknown }) as Product;
  };

  /**
   * Load products: SERVER IS SINGLE SOURCE OF TRUTH.
   * Uses per-warehouse cache (TTL 60s): on warehouse switch show cached list immediately and refresh in background.
   * Uses resilient client (retries, circuit breaker). On failure → fallback to localStorage/IndexedDB.
   * @param signal - AbortSignal for cancellation (e.g. on unmount).
   * @param options.silent - If true, do not show full-page loading (for background refresh). Default false.
   * @param options.bypassCache - If true, always fetch from server (e.g. when opening Inventory page for fresh data).
   * @param options.append - If true, fetch next page (offset = current list length) and append; does not replace list.
   */
  const loadProducts = async (
    signal?: AbortSignal,
    options?: { silent?: boolean; bypassCache?: boolean; timeoutMs?: number; append?: boolean; requestLimit?: number }
  ) => {
    const silent = options?.silent === true;
    const bypassCache = options?.bypassCache === true;
    const timeoutMs = options?.timeoutMs;
    const append = options?.append === true;
    const wid = effectiveWarehouseIdRef.current;

    // Skip API when warehouse id is missing or all-zeros — backend returns 400 for unknown warehouse_id
    const isInvalidWarehouse = !wid || wid === '00000000-0000-0000-0000-000000000000';
    if (isInvalidWarehouse) {
      setProducts([]);
      setIsLoading(false);
      setError(null);
      setBackgroundRefreshing(false);
      if (!append) setProductsTotal(null);
      return;
    }

    // Load next page: append only (API-only path)
    if (append) {
      if (loadMoreInflightRef.current) return;
      loadMoreInflightRef.current = true;
      setIsLoadingMore(true);
      try {
        const offset = productsRef.current.length;
        const limit = options?.requestLimit ?? INVENTORY_PAGE_SIZE;
        const getOpts = { signal, timeoutMs: timeoutMs ?? INVENTORY_API_TIMEOUT_MS, maxRetries: 0 };
        let raw: { data?: Product[]; total?: number } | Product[] | null = null;
        try {
          raw = await apiGet<{ data?: Product[]; total?: number } | Product[]>(
            API_BASE_URL,
            productsPath('/api/products', { limit, offset, view: 'list' }),
            getOpts
          );
        } catch (e) {
          const status = (e as { status?: number })?.status;
          if (status === 404) {
            raw = await apiGet<{ data?: Product[]; total?: number } | Product[]>(
              API_BASE_URL,
              productsPath('/admin/api/products', { limit, offset, view: 'list' }),
              getOpts
            );
          } else {
            throw e;
          }
        }
        const parsed = parseProductsResponse(raw);
        if (!parsed.success) return;
        const nextItems = parsed.items.map((p) => normalizeProduct(p));
        const totalFromApi = readTotalFromProductsResponse(raw);
        if (typeof totalFromApi === 'number') setProductsTotal(totalFromApi);
        setProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newOnly = nextItems.filter((p) => !existingIds.has(p.id));
          const ru = lastUpdatedProductRef.current;
          let next: Product[];
          if (ru && Date.now() - ru.at <= LAST_UPDATED_PRESERVE_MS) {
            next = [...prev, ...newOnly].map((p) => (p.id === ru.product.id ? ru.product : p));
          } else {
            next = [...prev, ...newOnly];
          }
          if (effectiveWarehouseIdRef.current === wid) {
            cacheRef.current[wid] = { data: next, ts: Date.now() };
            if (isStorageAvailable()) setStoredData(productsCacheKey(wid), stripHeavyImagesForCache(next));
          }
          return next;
        });
        setLastSyncAt(new Date());
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        reportError(err, { context: 'loadProductsAppend' });
      } finally {
        loadMoreInflightRef.current = false;
        setIsLoadingMore(false);
      }
      return;
    }

    const now = Date.now();
    if (silent) {
      if (now - lastSilentRefreshAtRef.current < SILENT_REFRESH_THROTTLE_MS) return;
      if (now - lastSizeUpdateAtRef.current < SIZE_UPDATE_COOLDOWN_MS) return;
      lastSilentRefreshAtRef.current = now;
    }

    if (loadProductsInflightRef.current) {
      if (silent) return;
      loadProductsAbortRef.current?.abort();
      return;
    }
    loadProductsInflightRef.current = true;
    const mainAbort = new AbortController();
    loadProductsAbortRef.current = mainAbort;
    const mergeAbortSignals = (a?: AbortSignal, b?: AbortSignal): AbortSignal | undefined => {
      if (!a) return b;
      if (!b) return a;
      const c = new AbortController();
      const onAbort = () => {
        c.abort();
      };
      a.addEventListener('abort', onAbort);
      b.addEventListener('abort', onAbort);
      return c.signal;
    };
    const effectiveSignal = mergeAbortSignals(signal, mainAbort.signal);

    const cached = cacheRef.current[wid];
    const cacheValid = !bypassCache && cached && (now - cached.ts) < PRODUCTS_CACHE_TTL_MS;
    if (cacheValid && cached.data.length > 0) {
      setProducts(cached.data);
      setIsLoading(false);
      setError(null);
    }
    try {
      // Show "Updating..." and hide empty state during any refresh (including silent) to avoid empty-state flash
      if (!cacheValid) setBackgroundRefreshing(true);
      if (!silent && !cacheValid) {
        setIsLoading(true);
        setError(null);
      }

      try {
        const path = productsPath('/api/products', { limit: INVENTORY_PAGE_SIZE, offset: 0, view: 'list' });
        // Fail fast on server/network errors so we show cached products instead of spinning (maxRetries: 0).
        const getOpts = { signal: effectiveSignal, timeoutMs: timeoutMs ?? INVENTORY_API_TIMEOUT_MS, maxRetries: 0 };
        let raw: { data?: Product[]; total?: number } | Product[] | null = null;
        try {
          raw = await apiGet<{ data?: Product[]; total?: number } | Product[]>(API_BASE_URL, path, getOpts);
        } catch (e) {
          const status = (e as { status?: number })?.status;
          // Only fall back to admin endpoint when /api/products is not found (404). Never on 403 — cashiers must use /api/products only.
          if (status === 404) {
            raw = await apiGet<{ data?: Product[]; total?: number } | Product[]>(
              API_BASE_URL,
              productsPath('/admin/api/products', { limit: INVENTORY_PAGE_SIZE, offset: 0, view: 'list' }),
              getOpts
            );
          } else {
            throw e;
          }
        }
        const totalFromApi = readTotalFromProductsResponse(raw);
        const parsed = parseProductsResponse(raw);
        if (!parsed.success) {
          setError(parsed.message);
          return;
        }
        const apiProducts = parsed.items.map((p) => normalizeProduct(p));
        const apiIds = new Set(apiProducts.map((p) => p.id));
        // Keep products that exist only locally (e.g. added while offline or when API failed) so inventory never vanishes
        const localOnly: Product[] = [];
        if (isStorageAvailable()) {
          try {
            const localList = getStoredData<any[]>(productsCacheKey(wid), []);
            const list = Array.isArray(localList) ? localList : [];
            for (const p of list) {
              if (!p || typeof p !== 'object' || !p.id || apiIds.has(p.id)) continue;
              try {
                localOnly.push(normalizeProduct(p));
                apiIds.add(p.id);
              } catch {
                /* skip malformed */
              }
            }
          } catch {
            /* ignore */
          }
        }
        let merged = [...apiProducts, ...localOnly];
        // Preserve images and sizes from cache when API returns product without them (e.g. after update, silent refresh would otherwise wipe)
        const cached = cacheRef.current[wid]?.data;
        if (cached?.length) {
          merged = merged.map((p) => {
            const c = cached.find((x: Product) => x.id === p.id);
            if (!c) return p;
            let next = p;
            if (Array.isArray(c.images) && c.images.length > 0 && (!p.images || p.images.length === 0)) {
              next = { ...next, images: c.images };
            }
            const apiHasNoRealSizes = !(Array.isArray(next.quantityBySize) && next.quantityBySize.length > 0) || isOnlySyntheticOneSize(next.quantityBySize);
            if ((c.sizeKind === 'sized' || (Array.isArray(c.quantityBySize) && c.quantityBySize.length > 0)) && apiHasNoRealSizes) {
              next = { ...next, sizeKind: c.sizeKind ?? next.sizeKind, quantityBySize: Array.isArray(c.quantityBySize) ? c.quantityBySize : [] };
            }
            return next;
          });
        }
        // Prefer current state when API returned synthetic "One size" but we have real S/M/L (avoids revert after size edit)
        const currentForMerge = productsRef.current;
        if (currentForMerge.length > 0) {
          merged = merged.map((p) => {
            const fromState = currentForMerge.find((x) => x.id === p.id);
            if (!fromState) return p;
            const apiSynthetic = isOnlySyntheticOneSize(p.quantityBySize);
            const stateHasRealSizes =
              fromState.sizeKind === 'sized' &&
              Array.isArray(fromState.quantityBySize) &&
              fromState.quantityBySize.length > 0 &&
              !isOnlySyntheticOneSize(fromState.quantityBySize);
            if (apiSynthetic && stateHasRealSizes) return fromState;
            return p;
          });
        }
        // If we just updated a product (with images/sizes), any loadProducts that runs afterward must keep that version
        const updated = lastUpdatedProductRef.current;
        if (updated && Date.now() - updated.at < RECENT_UPDATE_WINDOW_MS) {
          merged = merged.map((p) => (p.id === updated.product.id ? updated.product : p));
        }
        // When doing a silent refresh after load-more, keep already loaded tail pages
        // so the list doesn't collapse back to page 1 while the user is scrolling.
        if (silent && currentForMerge.length > merged.length) {
          const mergedIds = new Set(merged.map((p) => p.id));
          const carryOver = currentForMerge.filter(
            (p) => !mergedIds.has(p.id) && !recentlyDeletedIdsRef.current.has(p.id)
          );
          if (carryOver.length > 0) {
            merged = [...merged, ...carryOver];
          }
        }

        // Fallback only when API returned empty: use only this warehouse's cache so we never show another warehouse's data.
        if (merged.length === 0 && isStorageAvailable()) {
          if (effectiveWarehouseIdRef.current !== wid) {
            setBackgroundRefreshing(false);
            setIsLoading(false);
            return;
          }
          const toProducts = (list: any[]): Product[] => {
            const out: Product[] = [];
            for (const p of list) {
              if (p == null || typeof p !== 'object') continue;
              try {
                out.push(normalizeProduct(p));
              } catch {
                /* skip malformed */
              }
            }
            return out;
          };
          const raw = getStoredData<any[]>(productsCacheKey(wid), []);
          const fallback = toProducts(Array.isArray(raw) ? raw : []);
          if (fallback.length > 0) {
            setProducts(fallback);
            if (!silent) setError('Server returned no products for this warehouse. Showing last saved list.');
            logInventoryRead({ listLength: fallback.length, environment: 'cache-fallback' });
            return;
          }
        }
        let listToSet = merged;
        const recent = lastAddedProductRef.current;
        if (recent && Date.now() - recent.at < RECENT_ADD_WINDOW_MS) {
          const inMerged = merged.some((p) => p.id === recent.product.id);
          if (!inMerged) listToSet = [recent.product, ...merged];
          else {
            const pin = merged.find((p) => p.id === recent.product.id);
            if (pin) listToSet = [pin, ...merged.filter((p) => p.id !== recent.product.id)];
          }
        }
        listToSet = listToSet.filter((p) => !recentlyDeletedIdsRef.current.has(p.id));
        // Do not apply if user switched warehouse (avoids overwriting current selection with in-flight request).
        if (effectiveWarehouseIdRef.current !== wid) {
          setBackgroundRefreshing(false);
          setIsLoading(false);
          return;
        }
        const current = productsRef.current;
        // When API returned empty for this warehouse, show empty list so Dashboard/Inventory stats match the selected warehouse (do not keep previous warehouse's list).
        if (listToSet.length === 0 && !silent) {
          setError('Server returned no products for this warehouse.');
        }
        // During silent refresh, skip setState when list is equivalent (order-independent) to avoid list jitter on mobile
        const currentById = new Map(current.map((p) => [p.id, p]));
        const sameIds = current.length === listToSet.length && listToSet.every((p) => currentById.has(p.id));
        const sameData = sameIds && listToSet.every((p) => {
          const a = currentById.get(p.id);
          if (!a) return false;
          const aUtc = a.updatedAt instanceof Date ? a.updatedAt.getTime() : Number(a.updatedAt) || 0;
          const bUtc = p.updatedAt instanceof Date ? p.updatedAt.getTime() : Number(p.updatedAt) || 0;
          if (aUtc !== bUtc || Number(a.quantity ?? 0) !== Number(p.quantity ?? 0)) return false;
          if ((a.sizeKind ?? 'na') !== (p.sizeKind ?? 'na')) return false;
          const aSizes = Array.isArray(a.quantityBySize) ? a.quantityBySize : [];
          const bSizes = Array.isArray(p.quantityBySize) ? p.quantityBySize : [];
          if (aSizes.length !== bSizes.length) return false;
          return aSizes.every((s, j) => (bSizes[j] && s.sizeCode === bSizes[j].sizeCode && s.quantity === bSizes[j].quantity));
        });
        // When API returned empty for this warehouse, always update state so Dashboard/Inventory show 0 (never keep previous warehouse's list).
        const skipStateUpdate = listToSet.length > 0 && silent && sameData;
        // Keep server total in sync even when silent refresh skips list state update.
        // Without this, pagination can think there's no more data and show empty pages.
        if (typeof totalFromApi === 'number') {
          setProductsTotal(totalFromApi);
        } else if (!skipStateUpdate) {
          setProductsTotal(null);
        }
        if (!skipStateUpdate) {
          setProducts(listToSet);
          if (!silent) setError(null);
          setLastSyncAt(new Date());
        }
        cacheRef.current[wid] = { data: listToSet, ts: Date.now() };
        logInventoryRead({ listLength: listToSet.length, environment: import.meta.env.PROD ? 'production' : 'development' });
        // Persist per-warehouse list (including []) so Dashboard/Inventory never show another warehouse's cached data.
        if (isStorageAvailable()) {
          setStoredData(productsCacheKey(wid), stripHeavyImagesForCache(listToSet));
        }
      } catch (apiErr) {
        if (apiErr instanceof Error && apiErr.name === 'AbortError') return;
        throw apiErr;
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      reportError(err, { context: 'loadProducts' });
      const errMsg = err instanceof Error ? err.message : String(err);
      const status = (err as { status?: number })?.status;
      const isNetwork =
        /load failed|failed to fetch|network error|networkrequestfailed|temporarily unavailable/i.test(errMsg);
      let message: string;
      if (status === 404) {
        message =
          import.meta.env.PROD
            ? 'Products could not be loaded from the server. Try Retry, or sign out and back in.'
            : 'Products API not found (404). Ensure the backend is deployed and VITE_API_BASE_URL points to it (e.g. in Vercel env).';
      } else if (status === 403) {
        message = 'Access denied (403). Check your login and permissions.';
      } else if (status === 401) {
        message = 'Please log in again. Session may have expired.';
      } else if (status != null && status >= 500) {
        message = getUserFriendlyMessage(err);
      } else if (isNetwork) {
        message = 'Cannot reach the server. Check your connection and that the backend URL is correct.';
      } else {
        message = getUserFriendlyMessage(err) || 'Failed to load products. Please check your connection.';
      }
      if (!silent) setError(message);
      // On failure, only show this warehouse's cache so we never display another warehouse's stats.
      const localRaw = getStoredData<any[]>(productsCacheKey(effectiveWarehouseId), []);
      const localProducts = (Array.isArray(localRaw) ? localRaw : []).map((p: any) => normalizeProduct(p));
      setProducts(localProducts);
      if (localProducts.length > 0 && !silent) {
        setError(null);
        const now = Date.now();
        if (now - lastCachedToastAtRef.current >= CACHED_TOAST_COOLDOWN_MS) {
          lastCachedToastAtRef.current = now;
          showToast('warning', 'Showing cached data. Tap Retry to refresh.');
        }
      }
    } finally {
      loadProductsInflightRef.current = false;
      if (silent) setBackgroundRefreshing(false);
      setIsLoading(false);
    }
  };
  loadProductsRef.current = loadProducts;

  // On mount: show cached products immediately when available so entering Inventory doesn't show a full-screen "Loading products..." spinner. Then refresh from API (silent if we had cache).
  useEffect(() => {
    clearMockData();
    mountedRef.current = true;
    const ac = new AbortController();
    let hadCache = false;

    // Always clear first so we never show the previous warehouse's stats under the new warehouse's name.
    setProducts([]);
    setProductsTotal(null);
    setError(null);
    let productsFromCache = getCachedProductsForWarehouse(effectiveWarehouseId);
    if (productsFromCache.length === 0) {
      const fromQuery = getProductsListFromQueryCache(queryClient, effectiveWarehouseId);
      if (fromQuery.length > 0) {
        productsFromCache = posProductsToInventoryProducts(fromQuery);
      }
    }
    if (productsFromCache.length > 0) {
      setProducts(productsFromCache);
      setIsLoading(false);
      hadCache = true;
    } else {
      setIsLoading(true);
    }

    (async () => {
      await loadProducts(ac.signal, hadCache ? { silent: true, bypassCache: true } : { bypassCache: true });
    })();

    return () => {
      mountedRef.current = false;
      ac.abort();
    };
  }, [currentWarehouseId, queryClient]);

  // POS sale / order deduct: refresh inventory list and invalidate shared product query.
  useEffect(() => {
    const onInventoryUpdated = () => {
      void invalidateProductsQuery(queryClient, effectiveWarehouseId);
      void loadProductsRef.current(undefined, { silent: true, bypassCache: true });
    };
    window.addEventListener(INVENTORY_UPDATED_EVENT, onInventoryUpdated);
    return () => window.removeEventListener(INVENTORY_UPDATED_EVENT, onInventoryUpdated);
  }, [queryClient, effectiveWarehouseId]);

  // Real-time: poll when tab visible so all devices see server truth. 30s reduces jitter from aggressive refresh while keeping cross-device updates reasonable.
  useRealtimeSync({
    onSync: () => {
      void invalidateProductsQuery(queryClient, effectiveWarehouseId);
      return loadProducts(undefined, { silent: true, bypassCache: true });
    },
    intervalMs: INVENTORY_POLL_MS,
  });

  // When user returns to this tab, refetch from server so changes from other devices (e.g. deletes) show immediately.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadProductsRef.current(undefined, { silent: true, bypassCache: true }).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // When user clicks "Try again" on the server-unavailable banner, refetch products.
  useEffect(() => {
    const onRetry = () => loadProducts(undefined, { bypassCache: true });
    window.addEventListener('circuit-retry', onRetry);
    return () => window.removeEventListener('circuit-retry', onRetry);
  }, []);

  // Persist inventory per warehouse so list shows immediately on next login/refresh.
  useEffect(() => {
    if (!isLoading && products.length > 0 && isStorageAvailable()) {
      const ok = setStoredData(productsCacheKey(effectiveWarehouseId), stripHeavyImagesForCache(products));
      setStoragePersistFailed(!ok);
    }
  }, [products, isLoading, effectiveWarehouseId]);

  /** Single-product path for GET/DELETE: query params (Vercel-safe; path /api/products/:id not routed). */
  const productByIdPath = (base: string, productId: string) => {
    const params = new URLSearchParams();
    params.set('id', productId);
    params.set('warehouse_id', effectiveWarehouseId);
    return `${base}?${params.toString()}`;
  };

  /** Path for PUT/PATCH product (no id in path; send id in body). */
  const productUpdatePath = (base: string) => base;

  /** Minimal payload for API POST/PUT: only fields backend persists. Reduces payload size and avoids sending UI-only data. */
  /** When omitImagesForSync is true, images are sent as [] to avoid 413 (payload too large) from base64 images exceeding Vercel's body limit. */
  const productToPayload = (product: Product, options?: { omitImagesForSync?: boolean }): Record<string, unknown> => {
    const toIso = (d: Date | string | null | undefined) =>
      d instanceof Date ? d.toISOString() : d ?? null;
    const images = options?.omitImagesForSync
      ? []
      : (Array.isArray(product.images) ? product.images : []);
    return {
      id: product.id,
      sku: product.sku ?? '',
      barcode: product.barcode ?? '',
      name: product.name ?? '',
      description: product.description ?? '',
      category: product.category ?? '',
      tags: Array.isArray(product.tags) ? product.tags : [],
      quantity: Number(product.quantity ?? 0) || 0,
      costPrice: Number(product.costPrice ?? 0) || 0,
      sellingPrice: Number(product.sellingPrice ?? 0) || 0,
      reorderLevel: Number(product.reorderLevel ?? 0) || 0,
      location: product.location && typeof product.location === 'object' ? product.location : { warehouse: '', aisle: '', rack: '', bin: '' },
      supplier: product.supplier && typeof product.supplier === 'object' ? product.supplier : { name: '', contact: '', email: '' },
      images,
      expiryDate: toIso(product.expiryDate ?? null),
      createdBy: product.createdBy ?? '',
      createdAt: toIso(product.createdAt),
      updatedAt: toIso(product.updatedAt),
      ...(product.version !== undefined && { version: product.version }),
      sizeKind: product.sizeKind ?? 'na',
      ...(() => {
        const cleaned = sanitizeQuantityBySizeForApi(product.quantityBySize);
        return cleaned.length > 0 ? { quantityBySize: cleaned } : {};
      })(),
      ...((product as Product & { warehouseId?: string }).warehouseId != null && { warehouseId: (product as Product & { warehouseId?: string }).warehouseId }),
    };
  };

  /**
   * Push products that exist only in this browser's storage to the API
   * so they appear in all browsers/devices. Returns syncedIds so we can clear localOnlyIds.
   */
  const syncLocalInventoryToApi = async (): Promise<{ synced: number; failed: number; total: number; syncedIds: string[] }> => {
    let apiIds = new Set<string>();
    try {
      try {
        const res = await apiGet<{ data?: any[] } | any[]>(API_BASE_URL, productsPath('/admin/api/products'));
        const list = Array.isArray(res) ? res : (res && typeof res === 'object' && Array.isArray((res as { data?: any[] }).data) ? (res as { data: any[] }).data : []);
        apiIds = new Set(list.map((p: any) => p.id));
      } catch {
        const res = await apiGet<{ data?: any[] } | any[]>(API_BASE_URL, productsPath('/api/products'));
        const list = Array.isArray(res) ? res : (res && typeof res === 'object' && Array.isArray((res as { data?: any[] }).data) ? (res as { data: any[] }).data : []);
        apiIds = new Set(list.map((p: any) => p.id));
      }
    } catch {
      return { synced: 0, failed: 0, total: 0, syncedIds: [] };
    }

    let localRaw = getStoredData<any[]>(productsCacheKey(effectiveWarehouseId), []);
    if (!Array.isArray(localRaw) || localRaw.length === 0) localRaw = getStoredData<any[]>('warehouse_products', []);
    const localProducts = (Array.isArray(localRaw) ? localRaw : []).map((p: any) => normalizeProduct(p));
    const localOnly = localProducts.filter((p) => !apiIds.has(p.id));
    const total = localOnly.length;
    if (total === 0) return { synced: 0, failed: 0, total: 0, syncedIds: [] };

    const CONCURRENCY = 5;
    const syncedIds: string[] = [];
    let failed = 0;
    const runOne = async (product: Product): Promise<boolean> => {
      try {
        const payload = productToPayload(product, { omitImagesForSync: true });
        try {
          await apiPost(API_BASE_URL, '/admin/api/products', payload);
        } catch {
          await apiPost(API_BASE_URL, '/api/products', payload);
        }
        return true;
      } catch {
        return false;
      }
    };
    for (let i = 0; i < localOnly.length; i += CONCURRENCY) {
      const chunk = localOnly.slice(i, i + CONCURRENCY);
      const results = await Promise.all(chunk.map((p) => runOne(p)));
      chunk.forEach((p, j) => {
        if (results[j]) {
          syncedIds.push(p.id);
        } else {
          failed++;
        }
      });
    }
    const synced = syncedIds.length;
    if (syncedIds.length > 0) setLocalOnlyIds((prev) => {
      const next = new Set(prev);
      syncedIds.forEach((id) => next.delete(id));
      return next;
    });
    await loadProducts();
    return { synced, failed, total, syncedIds };
  };

  syncRef.current = syncLocalInventoryToApi;
  useEffect(() => {
    if (localOnlyIds.size === 0) return;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      syncRef.current?.().catch(() => {});
    }, 120_000);
    return () => clearInterval(interval);
  }, [localOnlyIds.size]);

  /**
   * Add product: offline-first when enabled; API-only uses true optimistic UI (Phase 4).
   * Optimistic (API-only): insert temp with _pending, then on success replace with server item; on failure remove temp and show error. No full refetch.
   * @returns The new product id (for undo when offline).
   */
  const addProduct = async (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'> & { warehouseId?: string }): Promise<string> => {
    if (!productData?.name?.trim?.()) throw new Error('Product name is required');
    const SAVE_TIMEOUT_MS = 10_000;
    if (import.meta.env?.DEV) {
      console.time('Total Save Time');
      console.time('Data Preparation');
    }
    setSavePhase('saving');
    setError(null);
    try {
      if (!getApiCircuitBreaker().allowRequest()) {
        throw new Error('Server is temporarily unavailable. Writes disabled until connection is restored.');
      }
      const tempId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const tempProduct: Product = {
        ...productData,
        id: tempId,
        createdAt: new Date(),
        updatedAt: new Date(),
        _pending: true,
      } as Product;
      setApiOnlyProductsState((prev) => [tempProduct, ...prev]);
      const payload: Record<string, unknown> = productToPayload({ ...tempProduct, _pending: undefined } as Product);
      if (productData.warehouseId?.trim()) payload.warehouseId = productData.warehouseId.trim();
      // Match updateProduct: sized creates must always send explicit quantityBySize from the form (sanitized),
      // not only whatever productToPayload derived from the merged temp product — avoids rare POST bodies
      // missing per-size rows while sizeKind stays 'sized' (server then rejects or mis-writes inventory).
      if (productData.sizeKind === 'sized') {
        const rows = sanitizeQuantityBySizeForApi(
          Array.isArray(productData.quantityBySize) ? productData.quantityBySize : []
        );
        // Create only persists rows with qty > 0 anyway; omitting zeros matches that and avoids huge POST bodies
        // (e.g. "Load EU sizes") that can confuse proxies or edge cases. Edits still send zeros via updateProduct.
        const rowsForCreate = rows.filter((r) => r.quantity > 0);
        payload.sizeKind = 'sized';
        payload.quantityBySize = rowsForCreate;
        payload.quantity = rowsForCreate.reduce((s, r) => s + r.quantity, 0);
      }
      if (import.meta.env?.DEV) {
        console.timeEnd('Data Preparation');
        console.time('API Request');
      }
      const postProduct = async (): Promise<Record<string, unknown> | null> => {
        const postOpts = { timeoutMs: SAVE_TIMEOUT_MS, idempotencyKey: tempId };
        try {
          return (await apiPost<Record<string, unknown>>(API_BASE_URL, productsPath('/admin/api/products'), payload, postOpts)) ?? null;
        } catch {
          return (await apiPost<Record<string, unknown>>(API_BASE_URL, productsPath('/api/products'), payload, postOpts)) ?? null;
        }
      };
      let created: Record<string, unknown> | null = null;
      try {
        created = await postProduct();
      } catch (firstErr) {
        setApiOnlyProductsState((prev) => prev.filter((p) => p.id !== tempId));
        throw firstErr;
      }
      if (created === null) {
        setApiOnlyProductsState((prev) => prev.filter((p) => p.id !== tempId));
        throw new Error('Failed to save product');
      }
      if (import.meta.env?.DEV) {
        console.timeEnd('API Request');
        console.time('State Update');
      }
      let normalized = (created as { id?: string }).id
        ? normalizeProduct(created as Record<string, unknown>)
        : ({ ...tempProduct, _pending: undefined, id: tempId } as Product);
      const resolvedId = normalized.id ?? tempId;
      // Preserve form data when API omits or returns zero so every detail entered is recorded in state
      const qApi = Number(normalized.quantity ?? 0) || 0;
      const qForm = Number(productData.quantity ?? 0) || 0;
      if (qForm > 0 && qApi === 0) {
        normalized = { ...normalized, quantity: qForm } as Product;
      }
      if (Number(productData.costPrice ?? 0) > 0 && Number(normalized.costPrice ?? 0) === 0) {
        normalized = { ...normalized, costPrice: Number(productData.costPrice) } as Product;
      }
      if (Number(productData.sellingPrice ?? 0) > 0 && Number(normalized.sellingPrice ?? 0) === 0) {
        normalized = { ...normalized, sellingPrice: Number(productData.sellingPrice) } as Product;
      }
      if (Number(productData.reorderLevel ?? 0) >= 0 && normalized.reorderLevel === undefined) {
        normalized = { ...normalized, reorderLevel: Number(productData.reorderLevel ?? 0) } as Product;
      }
      if (productData.location && typeof productData.location === 'object' && normalized.location && Object.values(normalized.location).every((v) => !v)) {
        normalized = { ...normalized, location: productData.location } as Product;
      }
      if (productData.supplier && typeof productData.supplier === 'object' && normalized.supplier && Object.values(normalized.supplier).every((v) => !v)) {
        normalized = { ...normalized, supplier: productData.supplier } as Product;
      }
      // Always apply form sizes when user submitted with sizes so list/cache never show new product without sizes (fix: sizes not showing after add)
      if (Array.isArray(productData.quantityBySize) && productData.quantityBySize.length > 0) {
        normalized = { ...normalized, quantityBySize: productData.quantityBySize } as Product;
      }
      if (productData.sizeKind) {
        normalized = { ...normalized, sizeKind: productData.sizeKind } as Product;
      }
      if (productData.warehouseId?.trim()) {
        normalized = { ...normalized, warehouseId: productData.warehouseId.trim() } as Product;
      }
      if (Array.isArray(normalized.images) && normalized.images.length > 0) setProductImages(resolvedId, normalized.images);
      lastAddedProductRef.current = { product: normalized, at: Date.now() };
      setApiOnlyProductsState((prev) => prev.map((p) => (p.id === tempId ? normalized : p)));
      cacheRef.current[effectiveWarehouseId] = { data: [normalized, ...apiOnlyProducts.filter((p) => p.id !== tempId)], ts: Date.now() };
      setLastSyncAt(new Date());
      if (isStorageAvailable()) {
        const nextList = [normalized, ...apiOnlyProducts.filter((p) => p.id !== tempId)];
        setStoredData(productsCacheKey(effectiveWarehouseId), stripHeavyImagesForCache(nextList));
      }
      logInventoryCreate({ productId: resolvedId, sku: productData.sku ?? '', listLength: apiOnlyProducts.length + 1 });
      showToast('success', 'Product saved.');
      if (import.meta.env?.DEV) {
        console.timeEnd('State Update');
        console.timeEnd('Total Save Time');
      }
      return resolvedId;
    } catch (err) {
      const status = (err as { status?: number })?.status;
      const msg =
        status === 401
          ? 'Your session expired. Please sign in again.'
          : getUserFriendlyMessage(err);
      showToast('error', msg);
      throw err;
    } finally {
      setSavePhase('idle');
    }
  };

  /**
   * Update product: offline-first when enabled; API-only when flag off.
   * Uses PUT response to update state immediately (no full refetch) so "Saving..." disappears quickly.
   */
  const updateProduct = async (id: string, updates: Partial<Product> & { warehouseId?: string }) => {
    const product = products.find((p: Product) => p.id === id);
    if (!product) throw new Error('Product not found');
    const payloadImages = Array.isArray(updates.images) ? updates.images : [];
    if (payloadImages.length > 0) setProductImages(id, payloadImages);
    const SAVE_TIMEOUT_MS = 10_000;
    if (import.meta.env?.DEV) {
      console.time('Total Update Time');
      console.time('API Request (update)');
    }
    setSavePhase('saving');
    setError(null);
    try {
      const updated: Product = { ...product, ...updates, updatedAt: new Date(), id: product.id };
      const isSized = updates.sizeKind === 'sized' || updated.sizeKind === 'sized';
      const payload = { ...productToPayload(updated), warehouseId: (updates.warehouseId ?? effectiveWarehouseId) || DEFAULT_WAREHOUSE_ID } as Record<string, unknown>;
      if (isSized) {
        payload.quantityBySize = sanitizeQuantityBySizeForApi(
          Array.isArray(updates.quantityBySize) ? updates.quantityBySize : []
        );
      }
      const putProduct = async (): Promise<Record<string, unknown> | null> => {
        try {
          return (await apiPut<Record<string, unknown>>(API_BASE_URL, productUpdatePath('/admin/api/products'), payload, { timeoutMs: SAVE_TIMEOUT_MS })) ?? null;
        } catch {
          return (await apiPut<Record<string, unknown>>(API_BASE_URL, productUpdatePath('/api/products'), payload, { timeoutMs: SAVE_TIMEOUT_MS })) ?? null;
        }
      };
      let fromApi: Record<string, unknown> | null = null;
      try {
        fromApi = await putProduct();
      } catch (firstErr) {
        if ((firstErr as { status?: number })?.status === 401 && (await tryRefreshSession())) {
          fromApi = await putProduct();
        } else {
          throw firstErr;
        }
      }
      if (import.meta.env?.DEV) {
        console.timeEnd('API Request (update)');
        console.time('State Update (update)');
      }
      const normalized = fromApi && (fromApi as { id?: string }).id
        ? normalizeProduct(fromApi as Record<string, unknown>)
        : updated;
      const apiHasImages = Array.isArray(normalized.images) && normalized.images.length > 0;
      // Keep images from our update if API response omits them (e.g. backend doesn't return base64)
      const withImages =
        apiHasImages
          ? normalized
          : { ...normalized, images: payloadImages.length > 0 ? payloadImages : (Array.isArray(updated.images) ? updated.images : []) };
      const sizeKind = updates.sizeKind ?? (withImages as Product).sizeKind ?? 'na';
      const rawApiSizes = (withImages as Product).quantityBySize;
      const apiQuantityBySize = Array.isArray(rawApiSizes) ? rawApiSizes : [];
      const apiHasRealSizes = apiQuantityBySize.length > 0 && !isOnlySyntheticOneSize(apiQuantityBySize);
      const formSentSizes = Array.isArray(updates.quantityBySize) && updates.quantityBySize.length > 0;
      // When user saved multiple sizes: prefer API if it returned real sizes; else use form data so UI shows S/M/L immediately (avoids ONESIZE × N until refetch)
      const quantityBySize =
        apiHasRealSizes
          ? apiQuantityBySize
          : formSentSizes
            ? updates.quantityBySize!
            : apiQuantityBySize.length > 0 ? apiQuantityBySize : [];
      const finalProduct = { ...withImages, sizeKind, quantityBySize } as Product;
      const newList = products.map((p) => (p.id === id ? finalProduct : p));
      if (payloadImages.length > 0) setProductImages(id, payloadImages);
      setApiOnlyProductsState(newList);
      cacheRef.current[effectiveWarehouseId] = { data: newList, ts: Date.now() };
      const at = Date.now();
      lastUpdatedProductRef.current = { product: finalProduct, at };
      if (isSized) lastSizeUpdateAtRef.current = at;
      if (isStorageAvailable()) setStoredData(productsCacheKey(effectiveWarehouseId), stripHeavyImagesForCache(newList));
      setLastSyncAt(new Date());
      logInventoryUpdate({ productId: id, sku: product.sku });
      showToast('success', 'Product updated.');
      if (import.meta.env?.DEV) {
        console.timeEnd('State Update (update)');
        console.timeEnd('Total Update Time');
      }
      // Do not refetch after update: API often omits images in GET, which would overwrite the list and make the new image vanish
    } catch (err) {
      const status = (err as { status?: number })?.status;
      const msg =
        status === 404
          ? 'Product was deleted on another device. The list will refresh.'
          : status === 401
            ? 'Your session expired. Please sign in again.'
            : getUserFriendlyMessage(err);
      showToast('error', msg);
      throw err;
    } finally {
      setSavePhase('idle');
    }
  };

  /**
   * Delete product: offline-first when enabled; API-only when flag off.
   * On success: remove from state immediately (optimistic) then refetch in background so other tabs/devices see update on next poll.
   */
  const deleteProduct = async (id: string) => {
    try {
      let mode: string | undefined;
      try {
        const res = await apiDelete<{ ok?: boolean; mode?: string }>(
          API_BASE_URL,
          productByIdPath('/api/products', id)
        );
        mode = res?.mode;
      } catch {
        const res = await apiDelete<{ ok?: boolean; mode?: string }>(
          API_BASE_URL,
          productByIdPath('/admin/api/products', id)
        );
        mode = res?.mode;
      }
      logInventoryDelete({ productId: id });
      recentlyDeletedIdsRef.current.add(id);
      setTimeout(() => recentlyDeletedIdsRef.current.delete(id), RECENT_DELETE_WINDOW_MS);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      loadProducts(undefined, { bypassCache: true, silent: true }).catch(() => {});
      showToast(
        'success',
        mode === 'archived' ? INVENTORY_ERRORS.productArchivedBecauseSold : INVENTORY_ERRORS.productRemoved
      );
    } catch (err) {
      const status = (err as { status?: number })?.status;
      const msg =
        status === 403
          ? "You don't have permission to delete products."
          : status === 401
            ? 'Your session expired. Please sign in again.'
            : getUserFriendlyMessage(err);
      showToast('error', msg);
      throw err;
    }
  };

  /**
   * Delete multiple products: offline-first when enabled; API-only when flag off.
   * On success: remove from state immediately (optimistic) then refetch in background.
   */
  const deleteProducts = async (ids: string[]) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    let archivedCount = 0;
    for (const id of ids) {
      try {
        let mode: string | undefined;
        try {
          const res = await apiDelete<{ ok?: boolean; mode?: string }>(
            API_BASE_URL,
            productByIdPath('/api/products', id)
          );
          mode = res?.mode;
        } catch {
          const res = await apiDelete<{ ok?: boolean; mode?: string }>(
            API_BASE_URL,
            productByIdPath('/admin/api/products', id)
          );
          mode = res?.mode;
        }
        if (mode === 'archived') archivedCount++;
        logInventoryDelete({ productId: id });
      } catch (err) {
        const status = (err as { status?: number })?.status;
        const msg =
          status === 403
            ? "You don't have permission to delete products."
            : status === 401
              ? 'Your session expired. Please sign in again.'
              : getUserFriendlyMessage(err);
        showToast('error', msg);
        throw err;
      }
    }
    ids.forEach((id) => {
      recentlyDeletedIdsRef.current.add(id);
      setTimeout(() => recentlyDeletedIdsRef.current.delete(id), RECENT_DELETE_WINDOW_MS);
    });
    setProducts((prev) => prev.filter((p) => !idSet.has(p.id)));
    loadProducts(undefined, { bypassCache: true, silent: true }).catch(() => {});
    if (archivedCount > 0 && archivedCount === ids.length) {
      showToast('success', INVENTORY_ERRORS.productArchivedBecauseSold);
    } else if (archivedCount > 0) {
      showToast(
        'success',
        `${ids.length - archivedCount} removed. ${archivedCount} hidden from inventory (sales history kept).`
      );
    } else {
      showToast('success', INVENTORY_ERRORS.productRemoved);
    }
  };

  const getProduct = (id: string) => {
    return products.find((p: Product) => p.id === id);
  };

  const searchProducts = (query: string) => {
    if (!query || query.trim() === '') return products;
    
    const lowercaseQuery = query.toLowerCase().trim();
    return products.filter((p: Product) => {
      if (!p) return false;
      return (
        (p.name?.toLowerCase().includes(lowercaseQuery)) ||
        (p.sku?.toLowerCase().includes(lowercaseQuery)) ||
        (p.barcode?.toLowerCase().includes(lowercaseQuery)) ||
        (p.description?.toLowerCase().includes(lowercaseQuery)) ||
        (p.tags?.some((tag: string) => tag.toLowerCase().includes(lowercaseQuery)))
      );
    });
  };

  const filterProducts = (filters: ProductFilters) => {
    return products.filter((p: Product) => {
      const qty = Number(p.quantity ?? 0) || 0;
      const reorder = Number(p.reorderLevel ?? 0) || 0;
      if (filters.category && getCategoryDisplay(p.category) !== filters.category) return false;
      if (filters.minQuantity !== undefined && qty < filters.minQuantity) return false;
      if (filters.maxQuantity !== undefined && qty > filters.maxQuantity) return false;
      if (filters.lowStock && !(qty > 0 && qty <= reorder)) return false;
      if (filters.outOfStock && qty !== 0) return false;
      if (filters.tag && !p.tags.includes(filters.tag)) return false;
      return true;
    });
  };

  const isUnsynced = (productId: string) => localOnlyIds.has(productId);

  const verifyProductSaved = async (productId: string): Promise<{ saved: boolean; product?: Product }> => {
    if (productId === SEED_PLACEHOLDER_PRODUCT_ID) return { saved: false };
    try {
      const found = await apiGet<any>(API_BASE_URL, productByIdPath('/api/products', productId));
      if (!found?.id) return { saved: false };
      const normalized = normalizeProduct(found);
      if (mountedRef.current) {
        setLocalOnlyIds((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
        setProducts((prev) => {
          const next = prev.map((p) => (p.id === productId ? normalized : p));
          persistProducts(next);
          return next;
        });
      }
      return { saved: true, product: normalized };
    } catch {
      try {
        const found = await apiGet<any>(API_BASE_URL, productByIdPath('/admin/api/products', productId));
        if (!found?.id) return { saved: false };
        const normalized = normalizeProduct(found);
        if (mountedRef.current) {
          setLocalOnlyIds((prev) => {
            const next = new Set(prev);
            next.delete(productId);
            return next;
          });
          setProducts((prev) => {
            const next = prev.map((p) => (p.id === productId ? normalized : p));
            persistProducts(next);
            return next;
          });
        }
        return { saved: true, product: normalized };
      } catch {
        return { saved: false };
      }
    }
  };

  /** Stable so Inventory page effect doesn't re-run in a loop when server is down (prevents list jitter). */
  const refreshProducts = useCallback((options?: { silent?: boolean; bypassCache?: boolean; timeoutMs?: number }) => {
    return loadProductsRef.current(undefined, { bypassCache: true, ...options });
  }, []);

  const hasMore = useMemo(
    () => productsTotal != null && productsTotal > 0 && products.length < productsTotal,
    [productsTotal, products.length]
  );

  const loadMore = useCallback(async () => {
    await loadProductsRef.current(undefined, { append: true, bypassCache: true, silent: true });
  }, []);

  const contextValue = useMemo(
    () => ({
      products: productsWithLocalImages,
      isLoading,
      error,
      addProduct,
      updateProduct,
      deleteProduct,
      deleteProducts,
      undoAddProduct: async () => {},
      getProduct,
      searchProducts,
      filterProducts,
      refreshProducts,
      productsTotal,
      isBackgroundRefreshing,
      syncLocalInventoryToApi,
      unsyncedCount: localOnlyIds.size,
      lastSyncAt,
      isUnsynced,
      verifyProductSaved,
      storagePersistFailed,
      savePhase,
      hasMore,
      loadMore,
      isLoadingMore,
    }),
    [
      productsWithLocalImages,
      isLoading,
      error,
      addProduct,
      updateProduct,
      deleteProduct,
      deleteProducts,
      getProduct,
      searchProducts,
      filterProducts,
      refreshProducts,
      productsTotal,
      isBackgroundRefreshing,
      syncLocalInventoryToApi,
      localOnlyIds.size,
      lastSyncAt,
      isUnsynced,
      verifyProductSaved,
      storagePersistFailed,
      savePhase,
      hasMore,
      loadMore,
      isLoadingMore,
    ]
  );

  return <InventoryContext.Provider value={contextValue}>{children}</InventoryContext.Provider>;
}

export function useInventory() {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within InventoryProvider');
  }
  return context;
}
